import { create } from 'zustand';

interface VersionManifest {
  version: string;
  buildId: string;
}

interface AppVersionState {
  /** Version des laufenden Bundles (Build-Zeit injiziert). */
  currentVersion: string;
  /** Version, die der Server aktuell ausliefert — null, solange nichts geprüft wurde. */
  latestVersion: string | null;
  /** true, sobald der Server ein anderes Bundle ausliefert als das laufende. */
  updateAvailable: boolean;
  /** Verhindert, dass derselbe Hinweis mehrfach angezeigt wird. */
  notified: boolean;

  check: () => Promise<void>;
  markNotified: () => void;
}

function isVersionManifest(value: unknown): value is VersionManifest {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as VersionManifest).buildId === 'string' &&
    typeof (value as VersionManifest).version === 'string'
  );
}

export const useAppVersionStore = create<AppVersionState>((set, get) => ({
  currentVersion: __APP_VERSION__,
  latestVersion: null,
  updateAvailable: false,
  notified: false,

  check: async () => {
    if (get().updateAvailable) return;

    try {
      // Cache-Buster zusätzlich zum no-store: manche Proxies ignorieren den Header.
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'omit',
      });
      if (!response.ok) return;

      const manifest: unknown = await response.json();
      if (!isVersionManifest(manifest)) return;

      set({
        latestVersion: manifest.version,
        updateAvailable: manifest.buildId !== __BUILD_ID__,
      });
    } catch {
      // Absichtlich still: kein Netz, Dev-Server liefert index.html statt JSON,
      // SWA antwortet kurzzeitig mit HTML. Ein Versions-Check darf nie einen
      // Fehler in die UI tragen.
    }
  },

  markNotified: () => set({ notified: true }),
}));

export default useAppVersionStore;
